import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { initResponsiveScaling } from './utils/responsive-scaling';
import { preloadFeatureFlags } from './hooks/useFeatureFlags';
import { initGA, trackPageView } from './lib/analytics';
import { initClarity } from './lib/clarity';
import { initMetaPixel } from './lib/metaPixel';
import { captureUTMParameters } from './lib/utm';
import PostsPage from './pages/PostsPage';
import PostDetailPage from './pages/PostDetailPage';
import CreatePostPage from './pages/CreatePostPage';
import EditPostGroupPage from './pages/EditPostGroupPage';
import SchedulePage from './pages/SchedulePage';


import PricingPage from './pages/PricingPage';
import MediaPage from './pages/MediaPage';
import { Toaster } from 'react-hot-toast';
import './styles/toast-theme.css';

// Context providers
import { AuthProvider } from './contexts/AuthContext';
import { ClientProvider } from './contexts/ClientContext';
import { TourProvider } from './contexts/TourContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { AITaskProvider } from './contexts/AITaskContext';


// Layout components
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Auth components
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import PasswordResetForm from './components/auth/PasswordResetForm';
import PasswordResetConfirmForm from './components/auth/PasswordResetConfirmForm';
import EmailVerificationForm from './components/auth/EmailVerificationForm';
import { GoogleCallback } from './pages/auth/GoogleCallback';

// Page components
import LandingPage from './pages/LandingPage';
import LightThemeWrapper from './components/layout/LightThemeWrapper';
import DashboardPage from './pages/DashboardPage';
import AdStudioPage from './pages/AdStudioPage';
import LowCreditAlert from './components/credits/LowCreditAlert';

import AdStudioARNewPage from './pages/AdStudioARNew';
import AdStudioAR2Page from './pages/AdStudioAR2';
import AdStudioVideoPage from './pages/AdStudioVideo';
import OneClickCreatorPage from './pages/OneClickCreatorPage';
import TasksPage from './pages/TasksPage';
import ContentCreationPage from './pages/ContentCreationPage';

// Billing pages
import BillingPage from './pages/BillingPage';
import BillingSuccessPage from './pages/BillingSuccessPage';
import BillingCancelPage from './pages/BillingCancelPage';
import CreditsSuccessPage from './pages/CreditsSuccessPage';
import AdminDashboardPage from './pages/Admin/AdminDashboardPage'; // Import the admin dashboard
import AITaskCostsPage from './pages/Admin/AITaskCostsPage'; // Import the new page
import LaunchSuccessPage from './pages/Admin/LaunchSuccessPage'; // Import the launch success page

import TierManagementPage from './pages/Admin/TierManagementPage'; // Import the tier management page
import { AIModelsPage } from './pages/Admin/AIModelsPage'; // Import the AI models management page
import UserManagementPage from './pages/Admin/UserManagementPage'; // Import the user management page
import PricingManagementPage from './pages/Admin/PricingManagementPage'; // Import the pricing management page
import StripeSyncPage from './pages/Admin/StripeSyncPage'; // Import the Stripe sync page
import BrandKitPage from './pages/BrandKitPage';
import MultiImageTestPage from './pages/MultiImageTestPage';
import { TeamManagementPage } from './components/team/TeamManagementPage';
import { ErrorBoundary } from './components/ErrorBoundary';


import { ClientList, ClientForm, ClientDetail } from './components/client';
import { PostForm } from './components/post';
import { MediaLibrary } from './components/media';
import UserProfileForm from './components/auth/UserProfileForm';
import { ARAdCreator } from './components/ar/ARAdCreator';
import SocialConnectionsPage from './pages/SocialConnectionsPage';
import FacebookCallbackPage from './pages/FacebookCallbackPage';
import InstagramCallbackPage from './pages/InstagramCallbackPage';
import YouTubeCallbackPage from './pages/YouTubeCallbackPage';
import TwitterCallbackPage from './pages/TwitterCallbackPage';
import ComingSoonPage from './pages/ComingSoonPage';
import FeatureFlagsPage from './pages/Admin/FeatureFlagsPage';
import HelpPage from './pages/HelpPage';

// Legal pages
import LegalPage from './pages/LegalPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import DataUsagePage from './pages/DataUsagePage';
import DataDeletionPage from './pages/DataDeletionPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import GDPRCompliancePage from './pages/GDPRCompliancePage';
import PublicGDPRPage from './pages/PublicGDPRPage';

import ScrollToTop from './components/ScrollToTop';

// Company pages
import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import ContactPage from './pages/ContactPage';
import ResourcesPage from './pages/ResourcesPage';
import GettingStartedGuide from './pages/guides/GettingStartedGuide';
import ContentCreationTips from './pages/guides/ContentCreationTips';
import AIPromptingGuide from './pages/guides/AIPromptingGuide';
import BrandGuidelines from './pages/guides/BrandGuidelines';
import RoadmapPage from './pages/RoadmapPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Component to track page views
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

// Component to capture UTM parameters on initial load
function UTMCapture() {
  useEffect(() => {
    captureUTMParameters();
  }, []);

  return null;
}

function App() {
  // Initialize responsive scaling and preload feature flags on app load
  useEffect(() => {
    initResponsiveScaling();
    // Preload feature flags to prevent flash
    preloadFeatureFlags();

    // Initialize analytics tracking
    initGA();
    initClarity();
    initMetaPixel();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ClientProvider>
          <AITaskProvider>
            <TourProvider>
              <ThemeProvider>
                <SidebarProvider>
                  <Router>
                    <ScrollToTop />
                    <PageViewTracker />
                    <UTMCapture />
                    <LowCreditAlert threshold={50} criticalThreshold={10} />
                    <div className="min-h-screen bg-background">
                      <Routes>
                        {/* Public routes */}
                        <Route
                          path="/"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <LightThemeWrapper>
                                <LandingPage />
                              </LightThemeWrapper>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/login"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <LoginForm />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/register"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <RegisterForm />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/forgot-password" element={<PasswordResetForm />} />
                        <Route path="/reset-password" element={<PasswordResetConfirmForm />} />
                        <Route path="/verify-email" element={<EmailVerificationForm />} />
                        <Route path="/auth/google/callback" element={<GoogleCallback />} />
                        <Route path="callback/facebook" element={<FacebookCallbackPage />} />
                        <Route path="callback/instagram" element={<InstagramCallbackPage />} />
                        <Route path="callback/youtube" element={<YouTubeCallbackPage />} />
                        <Route path="callback/twitter" element={<TwitterCallbackPage />} />

                        {/* Public GDPR Request Page */}
                        <Route path="/gdpr-request" element={<PublicGDPRPage />} />
                        <Route
                          path="/pricing"
                          element={
                            <LightThemeWrapper>
                              <PricingPage />
                            </LightThemeWrapper>
                          }
                        />

                        {/* Billing success/cancel pages */}
                        <Route path="/billing/success" element={<BillingSuccessPage />} />
                        <Route path="/billing/cancel" element={<BillingCancelPage />} />
                        <Route path="/credits/success" element={<CreditsSuccessPage />} />

                        {/* Legal pages */}
                        <Route
                          path="/legal"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <LegalPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/privacy"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <PrivacyPolicyPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/terms"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <TermsOfServicePage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/data-usage"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <DataUsagePage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/data-deletion"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <DataDeletionPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/cookies"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <CookiePolicyPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Company pages */}
                        <Route
                          path="/about"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <AboutPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/blog"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <BlogPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/blog/:id"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <BlogPostPage />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/contact"
                          element={
                            <LightThemeWrapper>
                              <ContactPage />
                            </LightThemeWrapper>
                          }
                        />
                        <Route
                          path="/resources"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <ResourcesPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/resources/getting-started-guides"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <GettingStartedGuide />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/resources/content-creation-tips"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <ContentCreationTips />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/resources/ai-prompting-guide"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <AIPromptingGuide />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/resources/brand-guidelines"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <BrandGuidelines />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/roadmap"
                          element={
                            <ProtectedRoute requireAuth={false}>
                              <RoadmapPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Protected routes */}
                        <Route
                          path="/app"
                          element={
                            <ProtectedRoute>
                              <Layout />
                            </ProtectedRoute>
                          }
                        >
                          <Route index element={<Navigate to="/app/dashboard" replace />} />
                          <Route path="dashboard" element={<DashboardPage />} />

                          {/* Posts */}
                          <Route path="posts" element={<PostsPage />} />
                          <Route path="posts/new" element={<CreatePostPage />} />
                          <Route path="posts/:postId" element={<PostDetailPage />} />
                          <Route path="posts/:postId/edit" element={<PostForm mode="edit" />} />
                          <Route path="post-groups/:postGroupId/edit" element={<EditPostGroupPage />} />

                          {/* Media */}
                          <Route path="media" element={<MediaLibrary />} />

                          {/* Content Hub */}
                          <Route path="content-hub" element={<ContentCreationPage />} />
                          <Route path="content-hub/image-studio" element={<AdStudioPage />} />
                          <Route path="content-hub/3d-studio" element={<AdStudioARNewPage />} />
                          <Route path="content-hub/ar-studio" element={<AdStudioAR2Page />} />
                          <Route path="content-hub/video-studio" element={<AdStudioVideoPage />} />

                          {/* Legacy routes - redirect to new structure */}
                          <Route path="content-creation" element={<Navigate to="/app/content-hub" replace />} />
                          <Route path="content-creation/ad-studio" element={<Navigate to="/app/content-hub/image-studio" replace />} />
                          <Route path="content-creation/ad-studio-ar" element={<Navigate to="/app/content-hub/ar-studio" replace />} />
                          <Route path="content-creation/ad-studio-ar-new" element={<Navigate to="/app/content-hub/3d-studio" replace />} />
                          <Route path="content-creation/ad-studio-ar-2" element={<Navigate to="/app/content-hub/ar-studio" replace />} />
                          <Route path="content-creation/ad-studio-video" element={<Navigate to="/app/content-hub/video-studio" replace />} />
                          <Route path="one-click-creator" element={<OneClickCreatorPage />} />
                          <Route path="content-creation/tasks" element={<TasksPage />} />
                          <Route path="test/multi-image" element={<MultiImageTestPage />} />

                          {/* Clients */}
                          <Route path="clients" element={<ClientList />} />
                          <Route path="clients/new" element={<ClientForm mode="create" />} />
                          <Route path="clients/:clientId" element={<ClientDetail />} />
                          <Route path="clients/:clientId/edit" element={<ClientForm mode="edit" />} />
                          <Route path="clients/:clientId/brand-kit" element={<BrandKitPage />} />

                          {/* Team Management */}
                          <Route path="team" element={
                            <ErrorBoundary>
                              <TeamManagementPage />
                            </ErrorBoundary>
                          } />

                          {/* Profile */}
                          <Route path="profile" element={<UserProfileForm />} />

                          {/* Billing */}
                          <Route path="billing" element={<BillingPage />} />

                          {/* Help & Support */}
                          <Route path="help" element={<HelpPage />} />

                          {/* GDPR Compliance */}
                          <Route path="gdpr-compliance" element={<GDPRCompliancePage />} />

                          {/* AR Placement */}
                          <Route path="ar-placement" element={<ARAdCreator />} />

                          {/* Social Media Features */}
                          <Route path="schedule" element={<SchedulePage />} />

                          {/* Social Connections */}
                          <Route path="social-connections" element={<SocialConnectionsPage />} />

                          {/* Coming Soon */}
                          <Route path="coming-soon" element={<ComingSoonPage />} />

                          {/* Admin Routes */}
                          <Route path="admin/dashboard" element={<AdminDashboardPage />} />
                          <Route path="admin/ai-task-costs" element={<AITaskCostsPage />} />
                          <Route path="admin/launch-success" element={<LaunchSuccessPage />} />

                          <Route path="admin/tiers" element={<TierManagementPage />} />
                          <Route path="admin/ai-models" element={<AIModelsPage />} />
                          <Route path="admin/users" element={<UserManagementPage />} />
                          <Route path="admin/pricing" element={<PricingManagementPage />} />
                          <Route path="admin/feature-flags" element={<FeatureFlagsPage />} />
                          <Route path="admin/stripe-sync" element={<StripeSyncPage />} />
                        </Route>

                        {/* Catch all route */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>

                      {/* Toast notifications */}
                      <Toaster
                        position="top-center"
                        toastOptions={{
                          duration: 5000,
                          style: {
                            background: 'rgba(255, 255, 255, 0.95)',
                            color: '#1e293b',
                            padding: '16px',
                            borderRadius: '12px',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                            backdropFilter: 'blur(10px)',
                            maxWidth: '400px',
                          },
                          success: {
                            iconTheme: {
                              primary: '#10b981',
                              secondary: '#fff',
                            },
                            style: {
                              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)',
                              border: '1px solid rgba(16, 185, 129, 0.2)',
                            },
                          },
                          error: {
                            iconTheme: {
                              primary: '#ef4444',
                              secondary: '#fff',
                            },
                            style: {
                              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                            },
                          },
                        }}
                      />
                    </div>
                  </Router>
                </SidebarProvider>
              </ThemeProvider>
            </TourProvider>
          </AITaskProvider>
        </ClientProvider>
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;